import React, { useState } from 'react';
import styles from './index.module.sass';
import superagent from 'superagent';

/**
 * Categorías disponibles para subir videos.
 *
 * Importante:
 * Estos nombres deben coincidir exactamente con el campo `categoria`
 * que devuelve el webservice getVideos.
 */
const categorias = [
  'Creando sonrisas',
  'Folleto del Mes',
  'Video de Eventos',
  'Video de Productos',
  'Video de Reels',
  'Video de Tutoriales',
  'Video Corporativo',
];

/**
 * Componente SubirVideos
 *
 * Permite agregar videos a una categoría específica.
 *
 * Flujo principal:
 * 1. El usuario selecciona una categoría.
 * 2. Se consulta el webservice getVideos.
 * 3. Se busca la categoría seleccionada en la respuesta.
 * 4. Se calcula la siguiente secuencia disponible: secuencia mayor + 1.
 * 5. El campo secuencia se llena automáticamente y queda bloqueado.
 * 6. Al guardar, se envían categoría, título, link y secuencia a PostAgregarVideo.
 *
 * Nota:
 * Los textos del arreglo `categorias` deben coincidir exactamente con los valores
 * que devuelve el webservice, porque se usan para encontrar la categoría correcta.
 */
function SubirVideos({ langMenu, webserviceURL }) {
  // Loading usado únicamente al guardar el video.
  const [formData, setFormData] = useState({
    categoria: '',
    titulo: '',
    link: '',
    secuencia: '',
  });

  const [loading, setLoading] = useState(false);

  // Loading separado para evitar que el botón diga "Guardando"
  // mientras solo se está calculando la secuencia.
  const [loadingSecuencia, setLoadingSecuencia] = useState(false);

  // Mensaje visible para éxito, error o validaciones del formulario.
  const [mensaje, setMensaje] = useState('');

  /**
   * Actualiza los campos editables del formulario.
   *
   * Este handler se usa para título y link.
   * La categoría usa un handler separado porque al cambiarla
   * también debe calcularse automáticamente la secuencia.
   */
  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  /**
   * Consulta el webservice getVideos y calcula la siguiente secuencia
   * disponible para la categoría seleccionada.
   *
   * Estructura esperada del webservice:
   * [
   *   {
   *     categoria: 'Creando sonrisas',
   *     videos: [
   *       { enlace: '...', texto: '...', secuencia: 1 },
   *       { enlace: '...', texto: '...', secuencia: 2 }
   *     ]
   *   }
   * ]
   *
   * Regla de negocio:
   * La nueva secuencia debe ser la mayor secuencia existente + 1.
   *
   * Ejemplo:
   * Si la categoría tiene secuencias 1, 2, 3 y 4,
   * el nuevo video debe guardarse con secuencia 5.
   */
  const obtenerSecuenciaCategoria = (categoriaSeleccionada) => {
    setLoadingSecuencia(true);
    setMensaje('');

    const url = `${webserviceURL}/getVideos`;

    superagent
      .get(url)
      .set('Content-Type', 'application/json')
      .set('token', localStorage.getItem('TMBtoken'))
      .auth(localStorage.getItem('TMBtoken'), { type: 'bearer' })
      .then((res) => {
        //console.log('Respuesta consulta videos:', res.body);

        const categoriasResponse = Array.isArray(res.body) ? res.body : res.body?.data || [];

        const categoriaEncontrada = categoriasResponse.find(
          (item) => item.categoria === categoriaSeleccionada,
        );

        //console.log('Categoría encontrada:', categoriaEncontrada);

        const videos = categoriaEncontrada?.videos || [];

        const secuencias = videos
          .map((video) => Number(video.secuencia))
          .filter((secuencia) => !Number.isNaN(secuencia));

        const secuenciaActual = secuencias.length > 0 ? Math.max(...secuencias) : 0;

        const siguienteSecuencia = secuenciaActual + 1;

        //console.log('Secuencia actual:', secuenciaActual);
        //console.log('Siguiente secuencia:', siguienteSecuencia);

        setFormData((prevData) => {
          /**
           * Protección contra cambios rápidos:
           *
           * Si el usuario cambia de categoría antes de que responda el webservice,
           * evitamos escribir una secuencia que pertenezca a una categoría anterior.
           */
          if (prevData.categoria !== categoriaSeleccionada) {
            return prevData;
          }

          return {
            ...prevData,
            secuencia: siguienteSecuencia,
          };
        });
      })
      .catch((err) => {
        //console.error('Error al consultar secuencia:', err);
        setMensaje('No se pudo generar la secuencia automáticamente');

        setFormData((prevData) => ({
          ...prevData,
          secuencia: '',
        }));
      })
      .finally(() => {
        setLoadingSecuencia(false);
      });
  };

  /**
   * Maneja el cambio de categoría.
   *
   * Al seleccionar una nueva categoría:
   * 1. Guarda la categoría en el estado.
   * 2. Limpia la secuencia anterior.
   * 3. Consulta getVideos para calcular la siguiente secuencia.
   */
  const handleCategoriaChange = (event) => {
    const { value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      categoria: value,
      secuencia: '',
    }));

    if (value) {
      obtenerSecuenciaCategoria(value);
    }
  };

  /**
   * Envía el nuevo video al webservice PostAgregarVideo.
   *
   * A diferencia de getVideos, este endpoint sí usa POST
   * porque crea un nuevo registro.
   *
   * Datos enviados:
   * - categoria
   * - titulo
   * - link
   * - secuencia automática
   */
  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.categoria || !formData.titulo || !formData.link) {
      setMensaje('Categoría, título y link son obligatorios');
      return;
    }

    if (!formData.secuencia) {
      setMensaje('No se pudo generar la secuencia. Selecciona nuevamente la categoría');
      return;
    }

    setLoading(true);
    setMensaje('');

    const json = {
      categoria: formData.categoria,
      titulo: formData.titulo,
      link: formData.link,
      secuencia: formData.secuencia,
    };

    //console.log('JSON enviado a PostAgregarVideo:', json);

    const url = `${webserviceURL}/PostAgregarVideo`;

    superagent
      .post(url)
      .set('Content-Type', 'application/json')
      .send(json)
      .set('token', localStorage.getItem('TMBtoken'))
      .auth(localStorage.getItem('TMBtoken'), { type: 'bearer' })
      .then((res) => {
        const { body = {} } = res;

        const { typeMessage = '', message = '', messageCode = '' } = body;

        if (typeMessage === 'success' || messageCode === 200) {
          setMensaje('Video agregado correctamente');

          setFormData({
            categoria: '',
            titulo: '',
            link: '',
            secuencia: '',
          });
        } else {
          setMensaje(message || 'No se pudo agregar el video');
        }

        setLoading(false);
      })
      .catch((err) => {
        //console.error('Error al agregar video:', err);
        setMensaje('Ocurrió un error al agregar el video');
        setLoading(false);
      });
  };

  /**
   * Controla cuándo el botón Guardar debe verse activo.
   *
   * El formulario se considera completo cuando ya existe:
   * - categoría
   * - título
   * - link
   * - secuencia automática generada
   */
  const formularioCompleto =
    formData.categoria && formData.titulo && formData.link && formData.secuencia;

  return (
    <div className={styles.container}>
      <h2>{langMenu?.SubirVideos || 'Subir videos'}</h2>

      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.label}>
          {langMenu?.Categoria || 'Categoría'}
          <select
            name='categoria'
            value={formData.categoria}
            onChange={handleCategoriaChange}
            className={styles.input}>
            <option value=''>{langMenu?.SeleccionarCategoria || 'Seleccionar categoría'}</option>

            {categorias.map((categoria) => (
              <option key={categoria} value={categoria}>
                {categoria}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.label}>
          {langMenu?.Titulo || 'Título'}
          <input
            type='text'
            name='titulo'
            value={formData.titulo}
            onChange={handleChange}
            className={styles.input}
            placeholder='Experiencia XV Estrellas Adriana'
          />
        </label>

        <label className={styles.label}>
          {langMenu?.Link || 'Link'}
          <input
            type='text'
            name='link'
            value={formData.link}
            onChange={handleChange}
            className={styles.input}
            placeholder='1W7cSeR4RRU'
          />
        </label>

        <label className={styles.label}>
          {langMenu?.Secuencia || 'Secuencia'}
          {/* 
            La secuencia no debe capturarse manualmente.
            Se genera automáticamente al seleccionar categoría
            para evitar errores humanos en el orden de los videos.
          */}
          <input
            type='number'
            name='secuencia'
            value={formData.secuencia}
            readOnly
            className={styles.input}
            placeholder={loadingSecuencia ? 'Generando secuencia...' : 'Se genera automáticamente'}
          />
        </label>

        <button
          type='submit'
          className={`${styles.button} ${
            formularioCompleto ? styles.buttonActive : styles.buttonDisabled
          }`}
          disabled={!formularioCompleto || loading || loadingSecuencia}>
          {loading
            ? langMenu?.Guardando || 'Guardando...'
            : loadingSecuencia
              ? 'Generando secuencia...'
              : langMenu?.Guardar || 'Guardar'}
        </button>
      </form>

      {mensaje && <p className={styles.message}>{mensaje}</p>}
    </div>
  );
}

export default SubirVideos;
